import axios from "./AxiosClient";
import { Constants } from "@socialcoach/services";
import { scCatchServiceException } from "../../annotations";
import {
  CancelRequestOptions,
  Category,
  ContentPageTheme,
  CTA,
  EligibilityCriterion,
  ExtraFieldsParam,
  Media,
  Page,
  SuperPage,
  SuperPageAssignment,
  VideoScript,
} from "@/services/bedrock";

const SUPER_PAGE_SERVICE = `${Constants.API_URL}/bedrock/super_pages`;
const SUPER_PAGE_ASSIGNMENT_SERVICE = `${Constants.API_URL}/bedrock/super_page_assignments`;

export type GetSuperPagesParams = {
  playerId?: number;
  search?: string;
  completed?: boolean;
  viewed?: boolean;
  categoryId?: number | number[] | null;
  uncategorized?: boolean;
  status?: string | null | undefined;
  page?: number;
  perPage?: number;
  sort?: "created_date" | "updated_date" | "title";
  order?: "asc" | "desc";
  extraFields?: (keyof SuperPage.ExtraFields)[];
};

export type GetSuperPageAssignmentsParams = {
  superPageId?: number;
  search?: string;
  completed?: boolean;
  viewed?: boolean;
  status?: SuperPageAssignment.Status;
  categoryId?: number | number[] | null;
  uncategorized?: boolean;
  page?: number;
  perPage?: number;
  sort?:
    | "created_date"
    | "updated_date"
    | "first_name"
    | "last_name"
    | "email"
    | "assignment_status"
    | "view_count"
    | "click_count"
    | "relevance"
    | "super_page_title";
  order?: "asc" | "desc";
  extraFields?: ExtraFieldsParam;
};

export type SuperPageEligibilityValues = {
  id: number | null;
  eligibilityCriterions: EligibilityCriterion[];
};

export type SuperPageDetailValues = {
  id: number | null;
  title: string;
  pageTitle: string;
  status?: string;
  url?: string | null | undefined;
  categories: Category[];
  videoScript: VideoScript | null;
  originalMedia: Media[] | null;
  allowTitleChange: boolean;
  allowCtaChange: boolean;
  allowThemeChange: boolean;
  callToAction: CTA | null;
  contentPageTheme: ContentPageTheme | null;
};

export type UpdateSuperPageAssignmentsBulkResponse = {
  successes: number[];
  errors: { [key: number]: string };
};

export type RemindSuperPageAssignmentsBulkResponse = {
  successes: number[];
  errors: { [key: number]: string };
};

function convertToRequestData(form: Partial<SuperPageDetailValues>): any {
  const requestData: any = { ...form };
  if (form.originalMedia) {
    requestData.originalMedia = form.originalMedia;
  } else if (form.originalMedia === null) {
    requestData.originalMedia = [];
  }
  if (form.videoScript !== undefined) {
    requestData.videoScriptId = form.videoScript?.id ?? null;
    delete requestData.videoScript;
  }
  if (form.contentPageTheme !== undefined) {
    requestData.pageThemeId = form.contentPageTheme?.id ?? null;
    delete requestData.contentPageTheme;
  }
  return requestData;
}

export class SuperPageServices {
  @scCatchServiceException()
  static async createSuperPage(form: SuperPageDetailValues): Promise<SuperPage> {
    const requestData = convertToRequestData(form);
    const { data } = await axios.post<SuperPage>(SUPER_PAGE_SERVICE, requestData);
    return data;
  }

  @scCatchServiceException()
  static async convertToSuperPage(contentPageId: number): Promise<SuperPage> {
    const { data } = await axios.post<SuperPage>(SUPER_PAGE_SERVICE, { contentPageId });
    return data;
  }

  @scCatchServiceException()
  static async getSuperPages(
    params: GetSuperPagesParams = {},
    options?: CancelRequestOptions
  ): Promise<Page<SuperPage>> {
    const { data } = await axios.get(SUPER_PAGE_SERVICE, {
      params,
      ...options,
    });
    return data;
  }

  @scCatchServiceException()
  static async getSuperPage(superPageId: number, extraFields?: (keyof SuperPage.ExtraFields)[]): Promise<SuperPage> {
    const url = `${SUPER_PAGE_SERVICE}/${superPageId}`;
    const { data } = await axios.get(url, {
      params: { extraFields },
    });
    return data;
  }

  @scCatchServiceException()
  static async updateSuperPage(superPageId: number, form: Partial<SuperPageDetailValues>): Promise<SuperPage> {
    const url = `${SUPER_PAGE_SERVICE}/${superPageId}`;
    const requestData = convertToRequestData(form);
    const { data } = await axios.patch(url, requestData);
    return data;
  }

  @scCatchServiceException()
  static async deleteSuperPage(superPageId: number): Promise<void> {
    const url = `${SUPER_PAGE_SERVICE}/${superPageId}`;
    await axios.delete(url);
  }

  @scCatchServiceException()
  static async updateSuperPageAssignmentsBulk(
    superPageId: number,
    superPageAssignmentIds: number[],
    status: SuperPageAssignment.Status
  ): Promise<UpdateSuperPageAssignmentsBulkResponse> {
    const url = `${SUPER_PAGE_SERVICE}/${superPageId}/super_page_assignments/bulk_update`;
    const { data } = await axios.patch(url, { ids: superPageAssignmentIds, status });
    return data;
  }

  @scCatchServiceException()
  static async getSuperPageAssignments(
    params?: GetSuperPageAssignmentsParams,
    options?: CancelRequestOptions
  ): Promise<Page<SuperPageAssignment>> {
    const { superPageId, ...otherParams } = params ?? {};
    const url = superPageId
      ? `${SUPER_PAGE_SERVICE}/${superPageId}/super_page_assignments`
      : SUPER_PAGE_ASSIGNMENT_SERVICE;
    const { data } = await axios.get(url, { params: otherParams, ...options });
    return data;
  }

  @scCatchServiceException()
  static async getSuperPageAssignmentsOverview(options?: CancelRequestOptions): Promise<SuperPageAssignment.Overview> {
    const { data } = await axios.get(`${SUPER_PAGE_ASSIGNMENT_SERVICE}/overview`, options);
    return data;
  }

  @scCatchServiceException()
  static async getSuperPageAssignment(
    superPageAssignmentId: number,
    extraFields?: ExtraFieldsParam
  ): Promise<SuperPageAssignment> {
    const url = `${SUPER_PAGE_ASSIGNMENT_SERVICE}/${superPageAssignmentId}`;
    const { data } = await axios.get(url, { params: { extraFields } });
    return data;
  }

  @scCatchServiceException()
  static async updateSuperPageAssignment(
    superPageId: number,
    superPageAssignmentId: number,
    params: { useDefaultVideo?: boolean; status?: SuperPageAssignment.Status },
    extraFields?: ExtraFieldsParam
  ): Promise<SuperPageAssignment> {
    const url = `${SUPER_PAGE_SERVICE}/${superPageId}/super_page_assignments/${superPageAssignmentId}`;
    const { data } = await axios.patch(url, params, { params: { extraFields } });
    return data;
  }

  @scCatchServiceException()
  static async remindSuperPageAssignment(superPageId: number, superPageAssignmentId: number): Promise<void> {
    const url = `${SUPER_PAGE_SERVICE}/${superPageId}/super_page_assignments/${superPageAssignmentId}/remind`;
    await axios.post(url, {});
  }

  @scCatchServiceException()
  static async remindSuperPageAssignmentsBulk(
    superPageId: number,
    superPageAssignmentIds: number[]
  ): Promise<RemindSuperPageAssignmentsBulkResponse> {
    const url = `${SUPER_PAGE_SERVICE}/${superPageId}/super_page_assignments/remind`;
    const { data } = await axios.post(url, { ids: superPageAssignmentIds });
    return data;
  }
}
